/*
  AVM Herbals — shared add-to-cart helper (Phase 8)
  ---------------------------------------------------------------------------
  One small, dependency-free helper used by the Bundle section, the Quick View
  modal and the mobile sticky ATC bar so they all add to cart the same way and
  open the same drawer.

  This theme renders <avm-cart-drawer> (sections/cart-drawer-avm.liquid) instead
  of Craft’s stock <cart-drawer>, so Craft’s product-form.js has no cart element
  to call renderContents() on. The AVM drawer instead listens for the Craft
  pub/sub `cartUpdate` event (assets/constants.js + pubsub.js, loaded site-wide)
  and refreshes + opens itself for any source other than 'avm-cart-drawer'.

  So: POST the line item(s) to /cart/add.js in a single request, then publish
  `cartUpdate` — the drawer takes it from there. If there is no drawer on the
  page (or pub/sub is unavailable) we fall back to navigating to the cart page
  so the add is always visible to the shopper.
*/
(function () {
  'use strict';

  if (window.AVMCart) return;

  function cartAddUrl() {
    var base = (window.routes && window.routes.cart_add_url) || '/cart/add';
    return base + '.js';
  }

  function cartUrl() {
    return (window.routes && window.routes.cart_url) || '/cart';
  }

  function hasDrawer() {
    return !!document.querySelector('avm-cart-drawer');
  }

  function driveDrawer(drawer) {
    if (!drawer || typeof drawer.refreshSection !== 'function') return;
    drawer
      .refreshSection()
      .then(function () {
        if (typeof drawer.open === 'function') drawer.open();
      })
      .catch(function () {});
  }

  /*
    Refresh + open the AVM cart drawer (and notify other cartUpdate listeners).
    Craft’s publish() runs subscribers with Array.map and is NOT error-isolated,
    so a throwing subscriber could stop the drawer’s own subscriber from running.
    We therefore publish best-effort AND, if publishing threw, drive the drawer
    directly so it always opens. Returns true when a drawer is present.
  */
  function notifyDrawer(cartData) {
    var drawer = document.querySelector('avm-cart-drawer');
    var published = false;

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        var result = publish(PUB_SUB_EVENTS.cartUpdate, { source: 'avm-cart', cartData: cartData });
        if (result && typeof result.catch === 'function') result.catch(function () {});
        published = true;
      } catch (e) {
        published = false;
      }
    }

    if (!drawer) return false;
    /* If publish didn’t run cleanly, the drawer’s subscriber may not have fired. */
    if (!published) driveDrawer(drawer);
    return true;
  }

  window.AVMCart = {
    hasDrawer: hasDrawer,

    /*
      items: [{ id: <variantId>, quantity: <int> }, ...]
      Adds every line item in ONE request. Resolves with the /cart/add.js
      response, rejects with an Error (err.cart holds Shopify’s error body).
    */
    addItems: function (items) {
      return fetch(cartAddUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              var err = new Error((data && (data.description || data.message)) || 'Cart error');
              err.cart = data;
              throw err;
            }
            return data;
          });
        })
        .then(function (data) {
          var opened = notifyDrawer(data);
          /* Nothing to open → send the shopper to the cart so the add is visible. */
          if (!opened) window.location = cartUrl();
          return data;
        });
    },

    /* Convenience for a single variant. */
    addVariant: function (variantId, quantity) {
      return this.addItems([{ id: variantId, quantity: quantity || 1 }]);
    }
  };
}());

/* AVM Phase F QA fix — PDP hero 1:1 aspect ratio for square infographics.
   Inline styles set via JS override CSS class declarations (higher specificity).
   This runs on every page load where avm-cart.js is included (PDP sticky ATC). */
(function () {
  'use strict';
  function avmFixPdpHero() {
    document.querySelectorAll('.avm-gallery__hero').forEach(function (el) {
      el.style.aspectRatio = '1 / 1';
    });
    document.querySelectorAll('.avm-gallery__parallax img').forEach(function (el) {
      el.style.objectFit = 'contain';
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', avmFixPdpHero);
  } else {
    avmFixPdpHero();
  }
}());

/* AVM Phase F QA fix — sticky ATC desktop mode + scroll trigger.
   CSS hides .avm-sticky-atc on desktop (≥990px) unless --desktop class is present.
   The section JS is not adding --desktop or --shown; both are fixed here. */
(function () {
  'use strict';
  function avmFixStickyAtc() {
    var stickyBar = document.querySelector('.avm-sticky-atc');
    if (!stickyBar) return;

    /* 1. Desktop gate: add --desktop so the media-query allows display */
    if (window.matchMedia('(min-width: 990px)').matches) {
      stickyBar.classList.add('avm-sticky-atc--desktop');
    }

    /* 2. Show/hide trigger: watch the ATC button via IntersectionObserver.
          When the button leaves the viewport, show the sticky bar; when it returns, hide it. */
    var sentinel = document.querySelector('[name="add"]') ||
                   document.querySelector('.product-form__submit') ||
                   document.querySelector('.avm-pdp__info--sticky');
    if (!sentinel || !window.IntersectionObserver) return;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          stickyBar.classList.remove('avm-sticky-atc--shown');
        } else {
          stickyBar.classList.add('avm-sticky-atc--shown');
        }
      });
    }, { threshold: 0 });

    observer.observe(sentinel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', avmFixStickyAtc);
  } else {
    avmFixStickyAtc();
  }
}());
